import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { createContext, useContext } from 'react';
import { getProposalGenerationStatus } from 'api/operations/proposalServiceV3';
import ProposalDomainModule, { ProposalDomainParams } from 'store/modules/proposal/proposalDomainModule';
import { ProposalGenerationStatus } from 'types/proposalService';
import { bpHistory } from 'global/bpHistory';
import FirstStepController from '../../ui-stores/proposal-v2/proposal-generation/chooseTheWay/firstStepController';
import SecondStepController from '../../ui-stores/proposal-v2/proposal-generation/chooseTheWay/secondStepController';

type ControllerUnion = FirstStepController | SecondStepController;
type Controllers = [FirstStepController, SecondStepController];

export interface ChooseTheWayDomainParams extends ProposalDomainParams {
  urlParams: string;
  catalogId: string;
  proposalType: 'SHELF' | 'RFQ';
}

export default class ChooseTheWayPageStore {
  private _domainParams: ChooseTheWayDomainParams;
  private _isLoading = false;
  private _isInitialLoading = true;
  private _currentStepIndex: number = 0;
  private _steps: Controllers;
  private _proposalDomainModule = new ProposalDomainModule();
  private _generatedFilesStatus: ProposalGenerationStatus | null = null;

  public get currentStepIndex(): number {
    return this._currentStepIndex;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  public get isInitialLoading(): boolean {
    return this._isInitialLoading;
  }

  public get generatedFilesStatus(): ProposalGenerationStatus | null {
    return this._generatedFilesStatus;
  }

  public get areFilesIdentical(): boolean {
    return this._generatedFilesStatus === ProposalGenerationStatus.IDENTICAL;
  }

  public get areFilesOutdated(): boolean {
    return this._generatedFilesStatus === ProposalGenerationStatus.OUTDATED;
  }

  public get currentStepController(): ControllerUnion {
    const controller = this._steps[this._currentStepIndex];
    if (!controller) {
      throw new Error(`Failed to get step controller! Invalid current step index: ${this._currentStepIndex}!`);
    }
    return controller;
  }

  public get firstStepController(): FirstStepController {
    return this._steps[0];
  }

  public get secondStepController(): SecondStepController {
    return this._steps[1];
  }

  public get wantsToUploadCustomFile(): boolean {
    return this.firstStepController.wantsToUploadCustomFile;
  }

  public setGeneratedFilesStatus(status: ProposalGenerationStatus): void {
    this._generatedFilesStatus = status;
  }

  public async preparePage(): Promise<void> {
    this.setIsInitialLoading(true);
    await this._proposalDomainModule.loadProposalDomainData(this._domainParams);
    await runInAction(async () => {
      const status = await getProposalGenerationStatus(
        this._domainParams.cartId,
        this._proposalDomainModule.proposalDomainData!.cart.lastUpdateTime,
      );
      runInAction(() => {
        this.setGeneratedFilesStatus(status);
        this.setIsInitialLoading(false);
      });
    });
  }

  public setIsLoading(status: boolean): void {
    this._isLoading = status;
  }

  public setIsInitialLoading(status: boolean): void {
    this._isInitialLoading = status;
  }

  public setStepIndex(index: number): void {
    if (index >= 0 && index < this._steps.length) {
      this._currentStepIndex = index;
    }
  }

  public redirectBack(): void {
    if (this._domainParams.proposalType === 'SHELF') {
      bpHistory.push(`/proposal-v2/${this._domainParams.urlParams}`);
    } else {
      bpHistory.push(`/rfq-proposal/${this._domainParams.urlParams}&type=release`);
    }
  }

  public onNavigateStepBack(): void {
    if (this._currentStepIndex > 0) {
      this.setStepIndex(this._currentStepIndex - 1);
    }
  }

  public async saveStep(): Promise<void> {
    if (!this._steps[this._currentStepIndex]) {
      throw new Error(`No step controller with such index: ${this._currentStepIndex}`);
    }
    await this._steps[this._currentStepIndex].completeStep(
      `${this._domainParams.urlParams}&proposalType=${this._domainParams.proposalType}`,
    );
    runInAction(() => {
      this.setStepIndex(this._currentStepIndex + 1);
    });
  }

  public async completeFlow(): Promise<void> {
    this.setIsLoading(true);
    await this.secondStepController.completeStep(this._domainParams.catalogId);
    runInAction(() => {
      bpHistory.push(
        `/generate-proposal/ready/${this._domainParams.urlParams}&proposalType=${this._domainParams.proposalType}`,
      );
    });
  }

  constructor(domainParams: ChooseTheWayDomainParams) {
    this._domainParams = domainParams;
    this._steps = [
      new FirstStepController(),
      new SecondStepController({
        proposalDomainModule: this._proposalDomainModule,
      }),
    ];
    makeObservable<
      ChooseTheWayPageStore,
      '_isLoading' | '_currentStepIndex' | '_steps' | '_generatedFilesStatus' | '_isInitialLoading'
    >(this, {
      _isLoading: observable,
      _currentStepIndex: observable,
      _steps: observable,
      _generatedFilesStatus: observable,
      _isInitialLoading: observable,
      areFilesOutdated: computed,
      currentStepIndex: computed,
      isLoading: computed,
      currentStepController: computed,
      firstStepController: computed,
      isInitialLoading: computed,
      secondStepController: computed,
      wantsToUploadCustomFile: computed,
      areFilesIdentical: computed,
      setIsLoading: action.bound,
      setStepIndex: action.bound,
      redirectBack: action.bound,
      onNavigateStepBack: action.bound,
      saveStep: action.bound,
      completeFlow: action.bound,
      preparePage: action.bound,
      setIsInitialLoading: action.bound,
    });
  }
}

export const ChooseTheWayPageStoreContext = createContext<ChooseTheWayPageStore | null>(null);

export function useChooseTheWayPageStore(): ChooseTheWayPageStore {
  const store = useContext(ChooseTheWayPageStoreContext);
  if (!store) {
    throw new Error('useChooseTheWayPageStore must be used within ChooseTheWayPageStoreContext.Provider');
  }
  return store;
}
